/**
 * PAGE: LISTE UTILISATEURS
 *
 * Liste des utilisateurs avec:
 * - Édition inline du rôle (Admin/User)
 * - Changement de mot de passe
 * - Accessible uniquement aux superadmin
 */

import type { ColumnDef } from '@tanstack/react-table';
import { ListFilter, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ChangePasswordModal } from '../components/modals/ChangePasswordModal';
import { CreateUserModal } from '../components/modals/CreateUserModal';
import { FiltersModal } from '../components/modals/FiltersModal';
import { RenameUserModal } from '../components/modals/RenameUserModal';
import { PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { TableCell } from '../components/table';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, DateCell } from '../components/tables/cells';
import { BulkActionsBar, ConfirmDialog, LoadingState, toast, Select } from '../components/ui';
import { useEnterpriseTable } from '../hooks/useEnterpriseTable';
import { useUsersManagement } from '../hooks/useUsers';
import type {
  ChangePasswordInput,
  CreateUserInput,
  UpdateUserFullNameInput,
} from '../schemas/user';
import type { FilterConfig } from '../types/filters';
import type { User, UserRole } from '../types/user';
import { USER_ROLES, USER_ROLE_LABELS } from '../types/user';

export function UsersListPage() {
  const {
    users,
    loading,
    createUser,
    updateUserFullName,
    updateUserRole,
    changeUserPassword,
    deleteUser,
  } = useUsersManagement();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    id?: string;
    email?: string;
  }>({ open: false });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Utilisation du hook générique pour gérer le tableau
  const table = useEnterpriseTable<User>({
    data: users,
    searchableFields: ['email', 'full_name'],
    initialPageSize: 10,
    mode: 'client',
  });

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        field: 'role',
        label: 'Rôle',
        type: 'select',
        operators: ['equals', 'notEquals'],
        options: USER_ROLES.map((r) => ({ value: r, label: USER_ROLE_LABELS[r] })),
      },
      {
        field: 'created_at',
        label: 'Date de création',
        type: 'date',
        operators: ['sameDay', 'before', 'after', 'between', 'isNotNull', 'isNull'],
      },
      {
        field: 'last_active_at',
        label: 'Dernière activité',
        type: 'date',
        operators: ['sameDay', 'before', 'after', 'between', 'isNotNull', 'isNull'],
      },
    ],
    []
  );

  // Changement de rôle en inline
  const handleRoleChange = useCallback(
    async (userId: string, newRole: UserRole) => {
      const success = await updateUserRole(userId, newRole);
      if (success) {
        toast.success('Rôle mis à jour');
      } else {
        toast.error('Erreur lors de la mise à jour du rôle');
      }
    },
    [updateUserRole]
  );

  // Ouvrir la modal de renommage
  const handleOpenRename = useCallback((user: User) => {
    setSelectedUser(user);
    setIsRenameModalOpen(true);
  }, []);

  // Renommer un utilisateur
  const handleRenameUser = useCallback(
    async (data: UpdateUserFullNameInput) => {
      if (!selectedUser) return;

      const success = await updateUserFullName(selectedUser.id, data.full_name);
      if (success) {
        toast.success('Nom mis à jour avec succès');
      } else {
        toast.error('Erreur lors de la mise à jour du nom');
      }
    },
    [selectedUser, updateUserFullName]
  );

  // Ouvrir la modal de changement de mot de passe
  const handleOpenChangePassword = useCallback((user: User) => {
    setSelectedUser(user);
    setIsChangePasswordModalOpen(true);
  }, []);

  // Créer un utilisateur
  const handleCreateUser = useCallback(
    async (data: CreateUserInput) => {
      const success = await createUser(data.email, data.password, data.full_name, data.role);
      if (success) {
        toast.success('Utilisateur créé avec succès');
      } else {
        toast.error("Erreur lors de la création de l'utilisateur");
      }
    },
    [createUser]
  );

  // Changer le mot de passe
  const handleChangePassword = useCallback(
    async (data: ChangePasswordInput) => {
      if (!selectedUser) return;

      const success = await changeUserPassword(selectedUser.id, data.newPassword);
      if (success) {
        toast.success('Mot de passe modifié avec succès');
      } else {
        toast.error('Erreur lors du changement de mot de passe');
      }
    },
    [selectedUser, changeUserPassword]
  );

  // Ouvrir la confirmation de suppression
  const handleDeleteUser = useCallback((user: User) => {
    setDeleteConfirm({ open: true, id: user.id, email: user.email });
  }, []);

  // Suppression en masse
  const confirmBulkDelete = useCallback(async () => {
    const toDelete = table.selectedItems.filter((u) => u.role !== 'superadmin');
    setBulkDeleteConfirm(false);
    const results = await Promise.all(toDelete.map((u) => deleteUser(u.id)));
    const successCount = results.filter(Boolean).length;
    const failCount = results.length - successCount;
    if (successCount > 0) toast.success(`${successCount} utilisateur(s) supprimé(s)`);
    if (failCount > 0) toast.error(`${failCount} suppression(s) échouée(s)`);
    table.clearSelection();
  }, [table, deleteUser]);

  // Confirmer la suppression
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm.id) return;

    try {
      const success = await deleteUser(deleteConfirm.id);
      if (success) {
        toast.success('Utilisateur supprimé');
      } else {
        toast.error("Erreur lors de la suppression de l'utilisateur");
      }
    } catch {
      toast.error("Erreur lors de la suppression de l'utilisateur");
    } finally {
      setDeleteConfirm({ open: false });
    }
  }, [deleteConfirm.id, deleteUser]);

  // Définition des colonnes
  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        id: 'select',
        size: 60,
        header: ({ table }) => (
          <CheckboxCell
            row={
              {
                getIsSelected: () => table.getIsAllRowsSelected(),
                toggleSelected: () => table.toggleAllRowsSelected(),
              } as any
            }
          />
        ),
        cell: ({ row }) => <CheckboxCell row={row} />,
      },
      {
        accessorKey: 'full_name',
        header: 'Nom',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        size: 200,
        enableSorting: true,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
        size: 250,
        enableSorting: true,
      },
      {
        accessorKey: 'role',
        header: 'Rôle',
        cell: ({ row }) => {
          // Ne pas permettre la modification du rôle superadmin
          if (row.original.role === 'superadmin') {
            return <TableCell className="font-semibold text-purple-600">Super Admin</TableCell>;
          }

          return (
            <div className="flex items-center">
              <Select
                variant="inline"
                value={row.original.role}
                onChange={(e) => handleRoleChange(row.original.id, e.target.value as UserRole)}
              >
                {USER_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {USER_ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            </div>
          );
        },
        size: 180,
        enableSorting: true,
      },
      {
        accessorKey: 'created_at',
        header: 'Date de création',
        cell: ({ getValue }) => <DateCell date={getValue() as string} />,
        size: 150,
        enableSorting: true,
      },
      {
        accessorKey: 'last_active_at',
        header: 'Dernière activité',
        cell: ({ getValue }) => <DateCell date={getValue() as string} />,
        size: 160,
        enableSorting: true,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1 w-full">
            {/* Bouton modifier le nom */}
            <button
              onClick={() => handleOpenRename(row.original)}
              className="flex items-center justify-center size-9 rounded-md border border-zinc-200 bg-white shadow-xs hover:bg-zinc-50 transition-colors"
              aria-label="Modifier le nom"
              title="Modifier le nom"
            >
              <Pencil className="size-4 text-zinc-900" />
            </button>

            {/* Bouton avec 3 points pour changer le mot de passe */}
            <button
              onClick={() => handleOpenChangePassword(row.original)}
              className="flex items-center justify-center size-9 rounded-md border border-zinc-200 bg-white shadow-xs hover:bg-zinc-50 transition-colors"
              aria-label="Changer le mot de passe"
              title="Changer le mot de passe"
            >
              <MoreHorizontal className="size-4 text-zinc-900" />
            </button>

            {/* Bouton supprimer (désactivé pour les superadmin) */}
            {row.original.role !== 'superadmin' && (
              <button
                onClick={() => handleDeleteUser(row.original)}
                className="flex items-center justify-center size-9 rounded-md border border-zinc-200 bg-white shadow-xs hover:bg-red-50 hover:border-red-200 transition-colors"
                aria-label="Supprimer"
                title="Supprimer"
              >
                <Trash2 className="size-4 text-zinc-900" />
              </button>
            )}
          </div>
        ),
        size: 120,
      },
    ],
    [handleRoleChange, handleOpenRename, handleOpenChangePassword, handleDeleteUser]
  );

  if (loading) return <LoadingState message="Chargement des utilisateurs..." />;

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Utilisateurs & rôles' }]} />

      {/* Separator global */}
      <div className="w-full h-px bg-zinc-200" />

      <PageContent
        title="Utilisateurs & rôles"
        description="Gérez les utilisateurs et leurs permissions"
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                value={table.searchTerm}
                onChange={(e) => table.setSearchTerm(e.target.value)}
                className="h-9 pl-3 pr-3 text-sm border border-zinc-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-colors w-64"
              />
            </div>
            <button
              onClick={() => setIsFiltersModalOpen(true)}
              className="relative flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
              aria-label="Filtres"
              title="Filtres"
            >
              <ListFilter className="size-4 text-zinc-900" />
              {table.activeFilters.length > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 bg-zinc-900 text-white text-[0.625rem] font-semibold rounded-full">
                  {table.activeFilters.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center justify-center size-9 rounded-md border border-zinc-900 bg-zinc-900 shadow-xs hover:bg-zinc-700 transition-colors"
              aria-label="Créer un utilisateur"
              title="Créer un utilisateur"
            >
              <span className="text-white text-lg leading-none">+</span>
            </button>
          </div>
        }
        footer={
          <Pagination
            currentPage={table.currentPage}
            totalPages={table.totalPages}
            itemsPerPage={table.itemsPerPage}
            totalItems={table.totalItems}
            displayedItems={table.displayedItemsCount}
            onPageChange={table.setCurrentPage}
            onItemsPerPageChange={table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={table.displayedData}
          columns={columns}
          onRowSelectionChange={table.setSelectedItems}
          columnVisibility={table.columnVisibility}
          onColumnVisibilityChange={table.setColumnVisibility}
          pageSize={table.itemsPerPage}
          currentPage={table.currentPage}
          onPageChange={table.setCurrentPage}
          onPageSizeChange={table.setItemsPerPage}
          manualSorting={true}
          onSortChange={table.handleSortChange}
          activeSortField={table.sortField}
          activeSortDirection={table.sortDirection}
        />
      </PageContent>

      <FiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filterConfigs={filterConfigs}
        activeFilters={table.activeFilters}
        activeFilterMode={table.filterMode}
        onApply={table.setActiveFilters}
      />

      {/* Modal de création d'utilisateur */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
      />

      {/* Modal de renommage */}
      <RenameUserModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        userEmail={selectedUser?.email || ''}
        currentFullName={selectedUser?.full_name || ''}
        onSubmit={handleRenameUser}
      />

      {/* Modal de changement de mot de passe */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        userEmail={selectedUser?.email || ''}
        onSubmit={handleChangePassword}
      />

      {/* Dialog de confirmation de suppression individuelle */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open })}
        title={`Supprimer l'utilisateur ${deleteConfirm.email || ''} ?`}
        description="Cette action est irréversible. Toutes les données associées à cet utilisateur seront supprimées."
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={confirmDelete}
      />

      {/* Dialog de confirmation de suppression en masse */}
      {(() => {
        const deletable = table.selectedItems.filter((u) => u.role !== 'superadmin');
        const skipped = table.selectedItems.length - deletable.length;
        return (
          <ConfirmDialog
            open={bulkDeleteConfirm}
            onOpenChange={setBulkDeleteConfirm}
            title={`Supprimer ${deletable.length} utilisateur(s) ?`}
            description={[
              `Cette action est irréversible.`,
              skipped > 0
                ? ` ${skipped} compte(s) super admin dans la sélection ne seront pas supprimés.`
                : '',
            ].join('')}
            confirmLabel={`Supprimer ${deletable.length} utilisateur(s)`}
            variant="destructive"
            onConfirm={confirmBulkDelete}
          />
        );
      })()}

      {/* Barre d'actions en masse */}
      {table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={table.selectedItems.length}
          entityNamePlural="utilisateurs"
          onDelete={() => setBulkDeleteConfirm(true)}
          onExportCSV={() => {}}
          onClearSelection={table.clearSelection}
          canExport={false}
        />
      )}
    </PageLayout>
  );
}
